// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  Chip,
  DataTable,
  FieldSearchSelect,
  FieldText,
  Modal,
  SectionCard,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { versionBasePath } from "components/VersionManagementButtons";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";
import { useAddOwnershipByMemberIdResourceIdMutation } from "state/ownerships/ownershipsApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

function OwnershipsSection({ data: resource, onUpdate, isLoading, readOnly }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["resources", "community", "common"]);

  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ members: data && selectAllMembers(data) }),
    }
  );

  const ownerships = resource?.ownerships ?? [];
  const totalOwnership = resource?.total_ownership ?? 0;

  // Modal state
  const [open, setOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [percOwnership, setPercOwnership] = useState(0);
  const [startDate, setStartDate] = useState(lisbonToday());
  const [endDate, setEndDate] = useState(() => defaultBusinessEndDate(lisbonToday()));

  const handleClose = () => {
    setOpen(false);
    setSelectedMemberId(null);
    setPercOwnership(0);
    const nextStart = lisbonToday();
    setStartDate(nextStart);
    setEndDate(defaultBusinessEndDate(nextStart));
  };

  // Members not already owning this resource (for the add dropdown)
  const ownedMemberIds = new Set(ownerships.map((o) => o.user_id));
  const availableMembers = (members ?? [])
    .filter((m) => !ownedMemberIds.has(m.id))
    .map((m) => ({ value: m.id, label: m.name }));

  // Validation: remaining capacity
  const maxPercAllowed = 100 - totalOwnership;
  const isPercValid = parseFloat(percOwnership) > 0 && parseFloat(percOwnership) <= maxPercAllowed;

  // ADD
  const onAddClicked = () => {
    setSelectedMemberId(availableMembers[0]?.value ?? null);
    setPercOwnership(0);
    setOpen(true);
  };

  const [addOwnership, { isLoading: isAdding }] = useAddOwnershipByMemberIdResourceIdMutation();
  const onAddOwnership = async () => {
    try {
      await addOwnership({
        communityId: community.id,
        resourceId: resource.id,
        memberId: selectedMemberId,
        percOwnership: parseFloat(percOwnership),
        start_date: startDate,
        end_date: endDate || null,
      }).unwrap();
      handleClose();
    } catch (error) {
      dispatch(openNotificationPopup({ notification: error, type: "error" }));
    }
  };

  // DELETE
  const onDeleteOwnership = (userId) => {
    const action = () => {
      const newList = ownerships
        .filter((o) => o.user_id !== userId)
        .map((o) => ({ user_id: o.user_id, perc_ownership: o.perc_ownership }));
      onUpdate({ ownerships_payload: newList }, null);
    };
    dispatch(
      openWarningAlert({
        confirmText: t("single.confirm_remove"),
        confirmAction: action,
      })
    );
  };

  const rows = ownerships.map((o) => {
    const member = (members ?? []).find((m) => m.id === o.user_id);
    return {
      id: o.user_id,
      name: member?.name ?? o.user_id,
      perc: o.perc_ownership,
    };
  });

  const columns = [
    {
      key: "name",
      label: t("single.ownerships.table.member"),
      render: (v) => (
        <div className="rec-cell-member">
          <div className="rec-av-sm" style={{ background: avColor(v) }}>
            {initials(v)}
          </div>
          <b>{v}</b>
        </div>
      ),
    },
    {
      key: "perc",
      label: t("single.ownerships.table.perc_ownership"),
      align: "right",
      render: (v) => <Chip tone="teal">{`${v}%`}</Chip>,
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.edit", { defaultValue: "Editar" })}
            onClick={(e) => {
              e.stopPropagation();
              navigate(versionBasePath(community.id, "ownership", `${resource.id}~${row.id}`), {
                state: {
                  label: `${resource.name} / ${row.name}`,
                  returnTo: `/communities/${community.id}/infrastructure/resources/${resource.id}`,
                },
              });
            }}
          >
            <Icon name="history" />
          </button>
          {!readOnly && (
            <button
              type="button"
              className="rec-icon-btn"
              title={t("table.actions.delete", { defaultValue: "Remover" })}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteOwnership(row.id);
              }}
            >
              <Icon name="delete" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <SectionCard
      id="ownerships"
      icon="groups"
      title={t("single.ownerships.title")}
      action={
        <Button
          size="sm"
          onClick={onAddClicked}
          disabled={totalOwnership >= 100 || isLoading || isAdding || !members}
        >
          <Icon name="add" />
          {t("single.ownerships.add_button")}
        </Button>
      }
    >
      <p className="text-[12.5px] text-ink-3 leading-[1.5] -mt-2 mb-4">
        {t("single.ownerships.total_owned", { value: totalOwnership })}
      </p>
      <div className="-mx-[var(--pad-card)] -mb-[var(--pad-card)]">
        <DataTable
          columns={columns}
          rows={rows}
          search={false}
          onRow={(row) => navigate(`/communities/${community.id}/members/${row.id}`)}
        />
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        title={t("single.ownerships.modal.title_add")}
        maxWidth={460}
        footer={
          <>
            <Button variant="ghost" onClick={handleClose}>
              {t("new.cancel", { ns: "community" })}
            </Button>
            <Button
              disabled={
                !selectedMemberId ||
                !isPercValid ||
                !startDate ||
                (endDate && endDate < startDate) ||
                isLoading ||
                isAdding
              }
              onClick={onAddOwnership}
            >
              {t("new.create", { ns: "community" })}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-[14px]">
          <FieldSearchSelect
            label={t("single.ownerships.table.member")}
            options={availableMembers}
            value={selectedMemberId ?? ""}
            onChange={setSelectedMemberId}
          />
          <FieldText
            label={`${t("single.ownerships.table.perc_ownership")} (max: ${maxPercAllowed}%)`}
            type="number"
            min={0}
            max={maxPercAllowed}
            suffix="%"
            value={percOwnership}
            onChange={setPercOwnership}
          />
          <div className="grid grid-cols-2 gap-[14px]">
            <ValidityDateRangePicker
              className="col-span-2"
              value={{ start: startDate, end: endDate }}
              onChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </div>
        </div>
      </Modal>
    </SectionCard>
  );
}

OwnershipsSection.defaultProps = {
  data: null,
  isLoading: false,
  onUpdate: () => {},
  readOnly: false,
};

OwnershipsSection.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    ownerships: PropTypes.arrayOf(
      PropTypes.shape({
        user_id: PropTypes.string,
        perc_ownership: PropTypes.number,
      })
    ),
    total_ownership: PropTypes.number,
  }),
  onUpdate: PropTypes.func,
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export default OwnershipsSection;
